import React, { useRef, useState, useEffect, useMemo } from 'react'
import './styles.css'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import dayjs, { Dayjs } from 'dayjs'
import {
  Box, Typography, TextField, InputAdornment,
  Popover, IconButton, Tooltip,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faMagnifyingGlass, faChevronLeft, faChevronRight, faXmark, faChevronDown,
} from '@fortawesome/free-solid-svg-icons'
import { colors } from '../../common/pallette/colors'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const THIS_YEAR = dayjs().year();
// Show a generous range: 30 years back to 10 years forward
const YEAR_RANGE = Array.from({ length: 41 }, (_, i) => THIS_YEAR - 30 + i);

/** Compact year ‹ › + month-pill picker with inline year-grid dropdown. */
const MonthYearPicker = ({
  value,
  onChange,
}: {
  value: Dayjs;
  onChange: (d: Dayjs) => void;
}) => {
  const [year, setYear] = useState(value.year());
  const [showYearGrid, setShowYearGrid] = useState(false);
  const selectedYearRef = useRef<HTMLDivElement | null>(null);

  // Scroll selected year into view whenever year grid opens
  useEffect(() => {
    if (showYearGrid && selectedYearRef.current) {
      selectedYearRef.current.scrollIntoView({ block: 'center', behavior: 'auto' });
    }
  }, [showYearGrid]);

  const handleYearClick = (y: number) => {
    setYear(y);
    setShowYearGrid(false);
  };

  return (
    <Box sx={{ p: '14px 16px 16px', width: 244 }}>
      {/* ── Header: ‹  [Year ▾]  › ───────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: '12px',
        }}
      >
        <IconButton
          size="small"
          onClick={() => { setYear((y) => y - 1); setShowYearGrid(false); }}
          sx={{ p: '4px', color: colors.blue.navy }}
        >
          <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '11px' }} />
        </IconButton>

        {/* Clickable year label — toggles year grid */}
        <Box
          onClick={() => setShowYearGrid((v) => !v)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            cursor: 'pointer',
            px: '10px',
            py: '4px',
            borderRadius: '8px',
            userSelect: 'none',
            transition: 'background 0.12s',
            '&:hover': { background: 'rgba(76,101,192,0.08)' },
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '15px', color: colors.black.dark }}>
            {year}
          </Typography>
          <FontAwesomeIcon
            icon={faChevronDown}
            style={{
              fontSize: '9px',
              color: colors.blue.navy,
              transition: 'transform 0.15s',
              transform: showYearGrid ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>

        <IconButton
          size="small"
          onClick={() => { setYear((y) => y + 1); setShowYearGrid(false); }}
          sx={{ p: '4px', color: colors.blue.navy }}
        >
          <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '11px' }} />
        </IconButton>
      </Box>

      {showYearGrid ? (
        /* ── Scrollable year grid ──────────────────────── */
        <Box
          sx={{
            maxHeight: '176px',
            overflowY: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '4px',
            pr: '2px',
            /* thin scrollbar */
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { background: '#D1D5DB', borderRadius: '4px' },
          }}
        >
          {YEAR_RANGE.map((y) => {
            const isSelected = y === year;
            return (
              <Box
                key={y}
                ref={isSelected ? selectedYearRef : null}
                onClick={() => handleYearClick(y)}
                sx={{
                  textAlign: 'center',
                  py: '6px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: isSelected ? 700 : 400,
                  cursor: 'pointer',
                  color: isSelected ? '#fff' : colors.black.dark,
                  backgroundColor: isSelected ? colors.blue.navy : 'transparent',
                  border: `1px solid ${isSelected ? colors.blue.navy : 'transparent'}`,
                  transition: 'all 0.1s',
                  userSelect: 'none',
                  '&:hover': {
                    backgroundColor: isSelected ? colors.blue.navy : 'rgba(76,101,192,0.08)',
                    color: isSelected ? '#fff' : colors.blue.navy,
                    border: `1px solid rgba(76,101,192,0.2)`,
                  },
                }}
              >
                {y}
              </Box>
            );
          })}
        </Box>
      ) : (
        /* ── 3 × 4 month grid ──────────────────────────── */
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
          {MONTHS.map((m, i) => {
            const isSelected = value.year() === year && value.month() === i;
            return (
              <Box
                key={m}
                onClick={() => onChange(dayjs(new Date(year, i, 1)))}
                sx={{
                  textAlign: 'center',
                  py: '7px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: isSelected ? 700 : 500,
                  cursor: 'pointer',
                  color: isSelected ? '#fff' : colors.black.dark,
                  backgroundColor: isSelected ? colors.blue.navy : 'transparent',
                  border: `1px solid ${isSelected ? colors.blue.navy : 'transparent'}`,
                  transition: 'all 0.12s',
                  userSelect: 'none',
                  '&:hover': {
                    backgroundColor: isSelected ? colors.blue.navy : 'rgba(76,101,192,0.08)',
                    color: isSelected ? '#fff' : colors.blue.navy,
                    border: `1px solid rgba(76,101,192,0.2)`,
                  },
                }}
              >
                {m}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

/** Return the event date closest to today (future-first, past as fallback). */
function getNearestEventDate(events: any[]): Dayjs | null {
  if (!events || events.length === 0) return null;
  const today = dayjs().startOf('day');
  let bestFuture: Dayjs | null = null;
  let bestPast: Dayjs | null = null;
  for (const ev of events) {
    const raw = ev.start ?? ev.date;
    if (!raw) continue;
    const d = dayjs(raw).startOf('day');
    if (!d.isValid()) continue;
    if (d.isSame(today) || d.isAfter(today)) {
      if (!bestFuture || d.isBefore(bestFuture)) bestFuture = d;
    } else {
      if (!bestPast || d.isAfter(bestPast)) bestPast = d;
    }
  }
  return bestFuture ?? bestPast;
}

const Calendar = (props: any) => {
  const { data, globalSearch } = props;
  const calendarRef = useRef<FullCalendar | null>(null);

  // nearestDate for initial jump uses all data (before search filter is known)
  const nearestDate = getNearestEventDate(data ?? []);
  const initialDate = nearestDate ?? dayjs();

  const [currentMonthLabel, setCurrentMonthLabel] = useState(
    initialDate.format('MMMM YYYY'),
  );
  // Initialise from globalSearch so switching to calendar view pre-fills the bar
  const [searchQuery, setSearchQuery] = useState(globalSearch ?? '');

  // Month/year jump picker
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [pickerValue, setPickerValue] = useState<Dayjs>(initialDate);

  const handlePickerChange = (newDate: Dayjs | null) => {
    if (!newDate) return;
    setPickerValue(newDate);
    setPickerAnchor(null);
    calendarRef.current?.getApi()?.gotoDate(newDate.toDate());
  };

  // Event detail popover
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);

  // Filter events on search, sorted by date
  const filteredEvents = useMemo(() => {
    const pool: any[] = data ?? [];
    const results = !searchQuery.trim()
      ? pool
      : pool.filter(
          (ev: any) =>
            ev.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            ev.extendedProps?.clubName?.toLowerCase().includes(searchQuery.toLowerCase()),
        );
    return [...results].sort((a, b) => {
      const da = dayjs(a.start ?? a.date);
      const db = dayjs(b.start ?? b.date);
      return da.isBefore(db) ? -1 : da.isAfter(db) ? 1 : 0;
    });
  }, [data, searchQuery]);

  // Index tracking which result the calendar is currently showing
  const [matchIndex, setMatchIndex] = useState(0);

  // When search changes, reset to first result and jump there
  useEffect(() => {
    setMatchIndex(0);
    if (filteredEvents.length > 0 && searchQuery.trim()) {
      const first = filteredEvents[0];
      const d = dayjs(first.start ?? first.date);
      if (d.isValid()) {
        calendarRef.current?.getApi()?.gotoDate(d.toDate());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Jump to a specific result by index
  const goToMatch = (idx: number) => {
    if (idx < 0 || idx >= filteredEvents.length) return;
    setMatchIndex(idx);
    const ev = filteredEvents[idx];
    const d = dayjs(ev.start ?? ev.date);
    if (d.isValid()) {
      calendarRef.current?.getApi()?.gotoDate(d.toDate());
    }
  };

  // Jump to nearest event month on mount
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (api) api.gotoDate(initialDate.toDate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep calendar search in sync with the global search bar
  useEffect(() => {
    setSearchQuery(globalSearch ?? '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalSearch]);

  // Imperatively sync filtered events into FullCalendar so search reliably updates
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.removeAllEvents();
    filteredEvents.forEach((ev: any) => api.addEvent(ev));
  }, [filteredEvents]);

  const prev = () => calendarRef.current?.getApi()?.prev();
  const next = () => calendarRef.current?.getApi()?.next();
  const goToday = () => calendarRef.current?.getApi()?.today();
  const goToNearestEvent = () => {
    if (!nearestDate) return;
    calendarRef.current?.getApi()?.gotoDate(nearestDate.toDate());
  };

  const handleEventClick = (info: any) => {
    setPopoverAnchor(info.el as HTMLElement);
    setSelectedEvent(info.event);
  };

  const closePopover = () => {
    setPopoverAnchor(null);
    setSelectedEvent(null);
  };

  const popoverOpen = Boolean(popoverAnchor);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* ── Custom header ───────────────────────────────── */}
      {/* Row 1: Month label (left) + nav controls (right) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: '6px',
          px: '2px',
        }}
      >
        {/* Month / Year — clickable, opens year/month picker */}
        <Box
          onClick={(e) => setPickerAnchor(e.currentTarget)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            cursor: 'pointer',
            borderRadius: '6px',
            px: '6px',
            py: '3px',
            mx: '-6px',
            userSelect: 'none',
            transition: 'background 0.15s',
            '&:hover': { background: 'rgba(76,101,192,0.07)' },
          }}
        >
          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 700,
              color: colors.black.dark,
              letterSpacing: '-0.2px',
              whiteSpace: 'nowrap',
            }}
          >
            {currentMonthLabel}
          </Typography>
          <FontAwesomeIcon
            icon={faChevronDown}
            style={{
              fontSize: '9px',
              color: colors.blue.navy,
              marginTop: '1px',
              transition: 'transform 0.15s',
              transform: pickerAnchor ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>

        {/* Nav controls (right side) — all in one tight group */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
          {/* Recent event jump — compact pill */}
          {nearestDate && (
            <Box
              onClick={goToNearestEvent}
              sx={{
                fontSize: '10px',
                fontWeight: 600,
                color: colors.blue.navy,
                background: 'rgba(76,101,192,0.08)',
                border: '1px solid rgba(76,101,192,0.22)',
                borderRadius: '20px',
                px: '8px',
                py: '2px',
                cursor: 'pointer',
                mr: '2px',
                whiteSpace: 'nowrap',
                userSelect: 'none',
                transition: 'background 0.15s',
                '&:hover': { background: 'rgba(76,101,192,0.16)' },
              }}
            >
              recent ↗
            </Box>
          )}

          {/* ‹ Today › — arrows flank Today so they're always visible together */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: `1px solid ${colors.grey.light}`,
              borderRadius: '6px',
              overflow: 'hidden',
            }}
          >
            <IconButton
              size="small"
              onClick={prev}
              sx={{
                p: '3px 5px',
                borderRadius: 0,
                color: colors.grey.dark,
                '&:hover': { color: colors.blue.navy, background: `${colors.blue.navy}0D` },
              }}
            >
              <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '10px' }} />
            </IconButton>

            <Box
              onClick={goToday}
              sx={{
                fontSize: '11px',
                fontWeight: 500,
                color: colors.grey.dark,
                cursor: 'pointer',
                px: '8px',
                py: '4px',
                borderLeft: `1px solid ${colors.grey.light}`,
                borderRight: `1px solid ${colors.grey.light}`,
                userSelect: 'none',
                whiteSpace: 'nowrap',
                transition: 'color 0.15s, background 0.15s',
                '&:hover': { color: colors.blue.navy, background: `${colors.blue.navy}0D` },
              }}
            >
              Today
            </Box>

            <IconButton
              size="small"
              onClick={next}
              sx={{
                p: '3px 5px',
                borderRadius: 0,
                color: colors.grey.dark,
                '&:hover': { color: colors.blue.navy, background: `${colors.blue.navy}0D` },
              }}
            >
              <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '10px' }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Row 2: Search bar — result count / nav lives entirely inside the bar */}
      <Box sx={{ mb: '8px' }}>
        <TextField
          size="small"
          placeholder="Search events…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  style={{ fontSize: '11px', color: colors.grey.dark }}
                />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                  {filteredEvents.length > 0 ? (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => goToMatch(matchIndex - 1)}
                        disabled={matchIndex === 0}
                        sx={{ p: '2px', color: colors.blue.navy, '&.Mui-disabled': { opacity: 0.3 } }}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '9px' }} />
                      </IconButton>
                      <Box
                        sx={{
                          fontSize: '10px',
                          fontWeight: 600,
                          color: colors.blue.navy,
                          background: 'rgba(76,101,192,0.1)',
                          borderRadius: '10px',
                          px: '7px',
                          py: '1px',
                          whiteSpace: 'nowrap',
                          userSelect: 'none',
                        }}
                      >
                        {matchIndex + 1} / {filteredEvents.length}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => goToMatch(matchIndex + 1)}
                        disabled={matchIndex === filteredEvents.length - 1}
                        sx={{ p: '2px', color: colors.blue.navy, '&.Mui-disabled': { opacity: 0.3 } }}
                      >
                        <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '9px' }} />
                      </IconButton>
                    </>
                  ) : (
                    /* "0 found" stays inside the bar — no extra height below */
                    <Box
                      sx={{
                        fontSize: '10px',
                        fontWeight: 600,
                        color: '#D94040',
                        background: 'rgba(217,64,64,0.08)',
                        borderRadius: '10px',
                        px: '7px',
                        py: '1px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      0 found
                    </Box>
                  )}

                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{ p: '2px', color: '#9AA3AF' }}
                  >
                    <FontAwesomeIcon icon={faXmark} style={{ fontSize: '10px' }} />
                  </IconButton>
                </Box>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              borderRadius: '20px',
              fontSize: '12px',
              backgroundColor: colors.grey.bright,
              '& fieldset': {
                border: `1px solid ${searchQuery && filteredEvents.length === 0
                  ? 'rgba(217,64,64,0.4)'
                  : colors.grey.light}`,
              },
              '&:hover fieldset': { border: `1px solid ${colors.grey.dark}` },
              '&.Mui-focused fieldset': { border: `1px solid ${colors.blue.navy}` },
            },
            '& input': { padding: '5px 4px', color: colors.black.dark },
          }}
        />
      </Box>

      {/* ── Calendar grid ───────────────────────────────── */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={false}
          editable={false}
          selectable={false}
          dayMaxEvents={3}
          weekends={true}
          expandRows={true}
          height="100%"
          events={filteredEvents}
          eventStartEditable={false}
          eventDurationEditable={false}
          eventClick={handleEventClick}
          // Render each event as a Tooltip-wrapped pill so hovering shows the full title + club
          eventContent={(arg) => {
            const title = arg.event.title;
            const club = arg.event.extendedProps?.clubName;
            const isCurrentMatch =
              searchQuery.trim() &&
              filteredEvents[matchIndex] &&
              arg.event.title === filteredEvents[matchIndex].title &&
              arg.event.startStr === (filteredEvents[matchIndex].start ?? filteredEvents[matchIndex].date);
            return (
              <Tooltip
                title={
                  <Box>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#fff' }}>{title}</Typography>
                    {club && <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)', mt: '2px' }}>{club}</Typography>}
                    <Typography sx={{ fontSize: '9px', color: 'rgba(255,255,255,0.6)', mt: '2px' }}>Click for details</Typography>
                  </Box>
                }
                placement="top"
                arrow
                enterDelay={200}
                enterNextDelay={100}
              >
                <Box
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                    fontSize: '9.5px',
                    fontWeight: 600,
                    color: '#fff',
                    px: '6px',
                    py: '2px',
                    borderRadius: '20px',
                    background: isCurrentMatch
                      ? '#2a3f9f'   // darker navy for current match
                      : '#4C65C0',
                    outline: isCurrentMatch ? '2px solid #fff' : 'none',
                    outlineOffset: '1px',
                    cursor: 'pointer',
                  }}
                >
                  {title}
                </Box>
              </Tooltip>
            );
          }}
          datesSet={(info) => {
            // currentStart is the 1st of the displayed month in dayGridMonth view
            setCurrentMonthLabel(
              dayjs(info.view.currentStart).add(15, 'day').format('MMMM YYYY'),
            );
          }}
          dayCellClassNames={(arg) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return arg.date < today ? ['fc-day-past'] : [];
          }}
        />
      </Box>

      {/* ── Month / Year jump picker ─────────────────────── */}
      <Popover
        open={Boolean(pickerAnchor)}
        anchorEl={pickerAnchor}
        onClose={() => setPickerAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 28px rgba(0,0,0,0.13)',
            border: '1px solid #EAECF0',
            overflow: 'hidden',
            mt: '4px',
            width: 260,
          },
        }}
      >
        <MonthYearPicker value={pickerValue} onChange={handlePickerChange} />
      </Popover>

      {/* ── Event detail popover ─────────────────────────── */}
      <Popover
        open={popoverOpen}
        anchorEl={popoverAnchor}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '10px',
            boxShadow: '0 8px 28px rgba(0,0,0,0.13)',
            border: '1px solid #EAECF0',
            minWidth: '220px',
            maxWidth: '280px',
            overflow: 'hidden',
            p: 0,
          },
        }}
      >
        {selectedEvent && (
          <>
            {/* Coloured header strip */}
            <Box
              sx={{
                background: 'rgba(76,101,192,0.07)',
                borderBottom: '1px solid #F0F2F5',
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 700,
                    color: colors.black.dark,
                    lineHeight: 1.35,
                    pr: 1,
                  }}
                >
                  {selectedEvent.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '11px',
                    color: colors.blue.navy,
                    mt: '3px',
                    fontWeight: 500,
                  }}
                >
                  {dayjs(
                    selectedEvent.start ?? selectedEvent.startStr,
                  ).format('ddd, MMM D YYYY')}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={closePopover}
                sx={{ mt: '-4px', mr: '-8px', color: '#9AA3AF', flexShrink: 0 }}
              >
                <FontAwesomeIcon icon={faXmark} style={{ fontSize: '11px' }} />
              </IconButton>
            </Box>

            {/* Body — club name if available */}
            {selectedEvent.extendedProps?.clubName && (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography
                  sx={{
                    fontSize: '10px',
                    color: '#9AA3AF',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    mb: '4px',
                    fontWeight: 600,
                  }}
                >
                  Club
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {selectedEvent.extendedProps?.clubLogo && (
                    <Box
                      component="img"
                      src={selectedEvent.extendedProps.clubLogo}
                      alt=""
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '4px',
                        objectFit: 'contain',
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: colors.black.dark,
                    }}
                  >
                    {selectedEvent.extendedProps.clubName}
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}
      </Popover>
    </Box>
  );
};

export default Calendar;
